import React, { Fragment } from 'react';
import { Table, Card } from 'react-bootstrap';
import { Match } from '../../models/interfaces/Match';
import { RosterEntry } from '../../models/interfaces/RosterEntry';
import PlayerView from '../players/PlayerView';
import { parseTeamColor } from '../../helpers/parseTeamColor';
import styled from 'styled-components';
import { MatchViewTextComponents } from '../../models/interfaces/ViewDescriptions/MatchViewDescription';
import { Link } from 'react-router-dom';

const TableHead = styled.th`
    box-sizing: border-box;
    -moz-box-sizing: border-box;
    -webkit-box-sizing: border-box;
    box-shadow: inset 0 -0.3rem 0 0 ${({ color }) => color};
`;

export interface PlayerSummary {
    player: RosterEntry;
    textComponents: MatchViewTextComponents;
}

interface Props {
    match: Match;
    playerSummaries: {
        title: string;
        lightCapPlayers: PlayerSummary[];
        darkCapPlayers: PlayerSummary[];
    }[];
    allowsLinkToPlayerProfile: boolean
    compact?: boolean;
    limit?: number;
}

export default function MatchPlayerSummary({
    match,
    playerSummaries,
    allowsLinkToPlayerProfile,
    compact = false,
    limit,
}: Props) {
    return (
        <>
            {playerSummaries.map((playerSummary) => {
                const playerLines = new Array<{
                    lightCapPlayerSummary?: PlayerSummary;
                    darkCapPlayerSummary?: PlayerSummary;
                }>();
                const lightCapStrings = playerSummary.lightCapPlayers;
                const lightCapLength = lightCapStrings.length;
                const darkCapStrings = playerSummary.darkCapPlayers;
                const darkCapLength = darkCapStrings.length;

                // default index but is overridden if a limit is provided & that limit is less then the indexed rows
                let maxIndex =
                    lightCapLength > darkCapStrings.length ? lightCapLength : darkCapLength;
                if (limit && limit < maxIndex) {
                    maxIndex = limit;
                }
                for (var i = 0; i < maxIndex; i++) {
                    playerLines.push({
                        lightCapPlayerSummary: i < lightCapLength ? lightCapStrings[i] : undefined,
                        darkCapPlayerSummary: i < darkCapLength ? darkCapStrings[i] : undefined,
                    });
                }
                return (
                    <Card className="mb-4 shadow-sm" key={playerSummary.title}>
                        <Card.Header>
                            {/* styled to be similar to a th */}
                            <p className="text-center mb-0">
                                <strong>{playerSummary.title}</strong>
                            </p>
                        </Card.Header>
                        <Table striped bordered className="mb-0" size={compact ? 'sm' : undefined}>
                            <thead>
                                <tr>
                                    <TableHead
                                        color={parseTeamColor(match.lightCapTeam.color)}
                                        className="text-right"
                                        style={{ width: '50%' }}
                                    >
                                        {match.lightCapTeam.name}
                                    </TableHead>
                                    <TableHead
                                        color={parseTeamColor(match.darkCapTeam.color)}
                                        style={{ width: '50%' }}
                                    >
                                        {match.darkCapTeam.name}
                                    </TableHead>
                                </tr>
                            </thead>
                            <tbody>
                                {playerLines.map((playerLine, i) => {
                                    return (
                                        <tr
                                            key={`${
                                                playerLine.lightCapPlayerSummary &&
                                                playerLine.lightCapPlayerSummary.textComponents.title
                                            }-${
                                                playerLine.darkCapPlayerSummary &&
                                                playerLine.darkCapPlayerSummary.textComponents.title
                                            }-${i}`}
                                        >
                                            <td className="text-right" style={{ width: '50%' }}>
                                                {playerLine.lightCapPlayerSummary ? (
                                                    <div style={{ border: `${playerLine.lightCapPlayerSummary.textComponents.isWarning ? '1px' : '0px'} solid red`}}>
                                                        {
                                                            allowsLinkToPlayerProfile && playerLine.lightCapPlayerSummary.player?.backingPlayer ?
                                                            <Link to={`/players/${playerLine.lightCapPlayerSummary.player.backingPlayer.id}`}> 
                                                                <PlayerView
                                                                    player={
                                                                        playerLine.lightCapPlayerSummary
                                                                            .player
                                                                    }
                                                                />
                                                            </Link>
                                                            :
                                                            <PlayerView
                                                                    player={
                                                                        playerLine.lightCapPlayerSummary
                                                                            .player
                                                                    }
                                                                />
                                                        }
                                                        
                                                        {compact ? (
                                                            <span>
                                                                {` - ${playerLine.lightCapPlayerSummary.textComponents.title}`}
                                                            </span>
                                                        ) : (
                                                            <>
                                                                <br />
                                                                <span>
                                                                    {
                                                                        playerLine
                                                                            .lightCapPlayerSummary
                                                                            .textComponents.title
                                                                    }
                                                                </span>
                                                            </>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <></>
                                                )}
                                            </td>
                                            <td style={{ width: '50%' }}>
                                                {playerLine.darkCapPlayerSummary ? (
                                                    <div style={{ border: `${playerLine.darkCapPlayerSummary.textComponents.isWarning ? '1px' : '0px'} solid red`}}>
                                                        {
                                                            allowsLinkToPlayerProfile && playerLine.darkCapPlayerSummary.player?.backingPlayer ?
                                                            <Link to={`/players/${playerLine.darkCapPlayerSummary.player.backingPlayer.id}`}> 
                                                                <PlayerView
                                                                    player={
                                                                        playerLine.darkCapPlayerSummary
                                                                            .player
                                                                    }
                                                                />
                                                            </Link>
                                                            :
                                                            <PlayerView
                                                                    player={
                                                                        playerLine.darkCapPlayerSummary
                                                                            .player
                                                                    }
                                                                />
                                                        }
                                                        {compact ? (
                                                            <span>
                                                                {` - ${playerLine.darkCapPlayerSummary.textComponents.title}`}
                                                            </span>
                                                        ) : (
                                                            <>
                                                                <br />
                                                                <span>
                                                                    {
                                                                        playerLine
                                                                            .darkCapPlayerSummary
                                                                            .textComponents.title
                                                                    }
                                                                </span>
                                                            </>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <></>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </Card>
                );
            })}
        </>
    );
}
